import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { getAvailableActions, type HiringAction } from "../../../utils";
import { type GetApplicationsResponse } from "../../../types";

interface Props {
  application: GetApplicationsResponse;
  isLoading: {
    isReviewing: boolean;
    isRejecting: boolean;
    isCreatingContract: boolean;
  };
  reviewingApplicationId: number | null;
  rejectingApplicationId: number | null;
  onAction: (action: HiringAction, applicationId: number, onClose?: () => void) => void;
  onDownloadProfile?: (id: number) => void;
  isDownloading?: boolean;
}

export default function ApplicationActionsButtons({
  application,
  isLoading,
  reviewingApplicationId,
  rejectingApplicationId,
  onAction,
}: Props) {
  const { t } = useTranslation("HiringPage");
  const { hasPermission } = useAuth();
  const isThisAppReviewing = reviewingApplicationId === application.id;
  const isThisAppRejecting = rejectingApplicationId === application.id;

  const { actions } = getAvailableActions(
    application,
    isThisAppReviewing || isThisAppRejecting || isLoading.isCreatingContract,
    t,
  );
  
  const buttonActions = actions.filter((action) => {
    if (action.id === "view_details") return false;
    
    if (action.id === "approve" || action.id === "reject" || action.id === "request_revision") {
      return hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
    }
    
    if (action.id === "create_contract") {
      return hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);
    }
    
    return true;
  });

  const hasButtons = buttonActions.length > 0;

  if (!hasButtons) {
    return null;
  }

  function getActionLoadingState(actionId: HiringAction): { isDisabled: boolean; loadingText: string } {
    const actionLoadingConfig: Record<
      HiringAction,
      { getIsLoading: () => boolean; getLoadingText: () => string }
    > = {
      approve: {
        getIsLoading: () => isThisAppReviewing,
        getLoadingText: () => t("actions.approving"),
      },
      request_revision: {
        getIsLoading: () => isThisAppReviewing,
        getLoadingText: () => t("modals.sending"),
      },
      reject: {
        getIsLoading: () => isThisAppRejecting,
        getLoadingText: () => t("actions.rejecting"),
      },
      create_contract: {
        getIsLoading: () => isLoading.isCreatingContract,
        getLoadingText: () => t("actions.creating"),
      },
      view_details: {
        getIsLoading: () => false,
        getLoadingText: () => "",
      },
    };

    const config = actionLoadingConfig[actionId];
    const isDisabled = config.getIsLoading();
    const loadingText = isDisabled ? config.getLoadingText() : "";

    return { isDisabled, loadingText };
  }

  return (
    <div className="flex flex-col gap-2 pt-3 border-t surface-base-stroke">
      {buttonActions.map((action) => {
        const { isDisabled, loadingText } = getActionLoadingState(action.id);

        return (
          <Button
            key={action.id}
            variant={action.variant}
            size="md"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onAction(action.id, application.id);
            }}>
            {action.icon}
            {isDisabled && loadingText ? loadingText : action.label}
          </Button>
        );
      })}
    </div>
  );
}
